import CloseOutlined from '@ant-design/icons/CloseOutlined'
import { Spin } from 'antd'
import CantileverButton from 'components/CantileverButton'
import { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/DocumentTableColumns'
import { DocumentHeaderTitle } from 'features/knowledgeDocumentViewer/components/toolbar/DocumentHeaderTitle'
import { useDocumentThumbnail } from 'features/knowledgeDocumentViewer/hooks/useDocumentThumbnail'
import styled from 'styled-components'

const Panel = styled.div<{
  $isOpen: boolean
  $width: number
}>`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: ${(props) => props.$width}px;
  color: ${(props) => props.theme.forecast.defaultText};
  border-left: 1px solid ${(props) => props.theme.forecast.border};
`
const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  color: ${(props) => props.theme.forecast.defaultText};
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 0.5rem 0.75rem;
`

const ThumbnailContainer = styled.div`
  width: 100%;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  background-color: ${(props) => props.theme.forecast.cardGroupBackground};
  padding: 0.5rem;
`

const ThumbnailImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`

const MetadataContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1rem;
  padding: 0.5rem 0.75rem;
`

const MetadataItem = styled.div`
  display: flex;
  flex-direction: column;
`

const MetadataLabel = styled.p`
  color: ${(props) => props.theme.forecast.secondaryText};
  font-size: 0.75rem;
  line-height: 1.2;
`

const ActionButtons = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  margin-top: auto;
  padding: 1rem 0.75rem;
`

interface DocumentPreviewPanelProps {
  document: DocumentTableRow | null
  isOpen: boolean
  isLoading?: boolean
  onClose: () => void
  onOpen: (doc: DocumentTableRow) => void
  onOpenNewTab?: (doc: DocumentTableRow) => void
}

export function DocumentPreviewPanel({
  document,
  isOpen,
  isLoading = false,
  onClose,
  onOpen,
  onOpenNewTab,
}: DocumentPreviewPanelProps) {
  const { thumbnailUrl, isLoading: isLoadingThumbnail } = useDocumentThumbnail({
    document,
    scale: 2,
  })

  const PANEL_WIDTH = 500

  if (!isOpen) {
    return null
  }

  if (isLoading || !document) {
    return (
      <Panel $isOpen={isOpen} $width={PANEL_WIDTH}>
        <PanelContainer>
          <Header>
            <div />
            <CantileverButton onClick={onClose} aria-label='Close preview'>
              <CloseOutlined />
            </CantileverButton>
          </Header>
          <ActionButtons>
            <CantileverButton primary disabled padding='0.5rem'>
              Open
            </CantileverButton>
            {onOpenNewTab && (
              <CantileverButton
                primary
                disabled
                padding='0.5rem'
                title='Open in new tab'>
                Open in new tab
              </CantileverButton>
            )}
          </ActionButtons>
        </PanelContainer>
      </Panel>
    )
  }

  return (
    <Panel $isOpen={isOpen} $width={PANEL_WIDTH}>
      <PanelContainer>
        <Header>
          <DocumentHeaderTitle documentId={document.id} />
          <CantileverButton onClick={onClose} aria-label='Close preview'>
            <CloseOutlined />
          </CantileverButton>
        </Header>

        <ThumbnailContainer>
          {isLoadingThumbnail ? (
            <Spin size='large' />
          ) : thumbnailUrl ? (
            <ThumbnailImage src={thumbnailUrl} alt={document.title} />
          ) : null}
        </ThumbnailContainer>

        <MetadataContainer>
          <MetadataItem>
            <MetadataLabel>System</MetadataLabel>
            <p>{document.system || '-'}</p>
          </MetadataItem>

          <MetadataItem>
            <MetadataLabel>Document Type</MetadataLabel>
            <p>{document.docType || '-'}</p>
          </MetadataItem>

          <MetadataItem>
            <MetadataLabel>Vendor</MetadataLabel>
            <p>{document.vendor || '-'}</p>
          </MetadataItem>

          <MetadataItem>
            <MetadataLabel>Drawing Number</MetadataLabel>
            <p>{document.drawing || '-'}</p>
          </MetadataItem>

          <MetadataItem>
            <MetadataLabel>Revision</MetadataLabel>
            <p>
              {document.revisionCode || '-'}
              {document.revisionDate && ` (${document.revisionDate})`}
            </p>
          </MetadataItem>
        </MetadataContainer>

        <ActionButtons>
          <CantileverButton
            primary
            onClick={() => onOpen(document)}
            padding='0.5rem'>
            Open
          </CantileverButton>
          {onOpenNewTab && (
            <CantileverButton
              primary
              onClick={() => onOpenNewTab(document)}
              padding='0.5rem'
              title='Open in new tab'>
              Open in new tab
            </CantileverButton>
          )}
        </ActionButtons>
      </PanelContainer>
    </Panel>
  )
}
